import { forwardRef } from 'react';

import { isObject } from '@infinex-private/ts-utils/object';
import type {
  AlignPhysicalX,
  AlignPhysicalY,
  ContentDistribution,
  Gap,
  Responsive,
} from '@infinex/ui-styles';
import { cn, useResponsiveProp } from '@infinex/ui-styles';

import type { ViewProps } from './View';
import { View } from './View';

// NOTE: Physical properties e.g. "top", "left" are used as prop-types to aid
// adoption with familiarity. They are however, properly applied as logical
// properties.
export type XStackProps = {
  /**
   * The alignment of items, relative to the container.
   * @default 'stretch'
   */
  align?: Responsive<AlignPhysicalY | 'stretch' | 'baseline'>;
  /**
   * How to justify items, relative to the container.
   * @default 'left'
   */
  justify?: Responsive<AlignPhysicalX | ContentDistribution>;
  /** The gap between the items. */
  gap?: Responsive<Gap>;
} & ViewProps;

/**
 * The `XStack` is a primitive component for composing layouts. It is a thin
 * wrapper around the `div` element, that uses flexbox to create horizontal
 * (X axis) stacks of content.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Basic_concepts_of_flexbox
 */
export const XStack = forwardRef<HTMLElement, XStackProps>(
  function XStack(props, ref) {
    const { align, gap, justify, className, style, ...viewProps } = props;
    const responsiveProp = useResponsiveProp();
    function responsiveMatch<T>(prop: Responsive<T>, value: T) {
      return isObject(prop) ? responsiveProp(prop) === value : prop === value;
    }

    return (
      <View
        ref={ref}
        className={cn(
          'flex flex-row',
          { 'gap-(--stack-gap)': gap != null },
          {
            'justify-start': responsiveMatch(justify, 'left'),
            'justify-center': responsiveMatch(justify, 'center'),
            'justify-end': responsiveMatch(justify, 'right'),
            'justify-between': responsiveMatch(justify, 'space-between'),
            'justify-around': responsiveMatch(justify, 'space-around'),
            'justify-evenly': responsiveMatch(justify, 'space-evenly'),
            // NOTE: The "stretch" implementation is non-standard. Stretching
            // is achieved by applying flex-grow to immediate children.
            '*:flex-1': responsiveMatch(justify, 'stretch'),
          },
          {
            'items-start': responsiveMatch(align, 'top'),
            'items-center': responsiveMatch(align, 'center'),
            'items-end': responsiveMatch(align, 'bottom'),
            'items-stretch': responsiveMatch(align, 'stretch'),
            'items-baseline': responsiveMatch(align, 'baseline'),
          },
          className
        )}
        style={{
          ...style,
          // @ts-expect-error custom properties are valid
          '--stack-gap': isObject(gap) ? responsiveProp(gap) : gap,
        }}
        {...viewProps}
      />
    );
  }
);
